"use client"

import { useEffect, useRef, useState } from "react"
import { REWARD_TIERS } from "@/lib/rewards"
import { usePrefersReducedMotion } from "@/components/glass/glass-kit"

/** Demo target — enough to read as real progress toward Platinum gear. */
const INITIAL_POINTS = 13_300
const TARGET_POINTS = 20_000
const PLATINUM_MIN = REWARD_TIERS.PLATINUM.min
const DURATION_MS = 3200

/**
 * Animated rewards balance + tier progress for landing money cards.
 * Mirrors the barber /rewards hero: points count up, purple bar fills in.
 */
export function RewardsProgressArt({ className = "" }: { className?: string }) {
  const reduced = usePrefersReducedMotion()
  const ref = useRef<HTMLDivElement>(null)
  const [points, setPoints] = useState(reduced ? TARGET_POINTS : INITIAL_POINTS)
  const [started, setStarted] = useState(reduced)

  useEffect(() => {
    if (reduced) return
    const el = ref.current
    if (!el) return

    let frame = 0
    let startMs = 0

    const run = (now: number) => {
      if (!startMs) startMs = now
      const t = Math.min((now - startMs) / DURATION_MS, 1)
      const eased = 1 - (1 - t) ** 3
      setPoints(Math.round(INITIAL_POINTS + (TARGET_POINTS - INITIAL_POINTS) * eased))
      if (t < 1) frame = requestAnimationFrame(run)
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setStarted(true)
        frame = requestAnimationFrame(run)
        io.disconnect()
      },
      { threshold: 0.35 },
    )
    io.observe(el)
    return () => {
      io.disconnect()
      if (frame) cancelAnimationFrame(frame)
    }
  }, [reduced])

  const progressPct = Math.min(100, Math.max(4, (points / PLATINUM_MIN) * 100))
  const ptsAway = Math.max(0, PLATINUM_MIN - points)
  const reachedPlatinum = points >= PLATINUM_MIN

  return (
    <div ref={ref} className={`absolute inset-x-0 bottom-0 ${className}`}>
      <div
        className="rounded-[20px] p-3.5 sm:p-4"
        style={{
          background: "var(--pg-card)",
          border: "1px solid var(--pg-card-edge)",
          boxShadow: "inset 0 1px 0 var(--pg-gloss)",
        }}
      >
        <p className="text-[9px] font-medium tracking-wide" style={{ color: "var(--pg-muted)" }}>
          Available Points
        </p>
        <p
          className="mt-0.5 text-[26px] font-light leading-none tracking-tight tabular-nums sm:text-[28px]"
          style={{ color: "var(--pg-fg)" }}
        >
          {points.toLocaleString()}
        </p>

        <div className="mt-3 space-y-1.5">
          <div className="flex items-center justify-between gap-2 text-[9px] sm:text-[10px]">
            <span style={{ color: "var(--pg-muted)" }}>
              {reachedPlatinum ? "Platinum tier" : "Progress to Platinum"}
            </span>
            <span className="font-semibold tabular-nums" style={{ color: "var(--pg-fg)", opacity: 0.85 }}>
              {reachedPlatinum ? "1.5× multiplier" : `${ptsAway.toLocaleString()} pts away`}
            </span>
          </div>
          <div
            className="relative h-1.5 w-full overflow-hidden rounded-full"
            style={{ background: "var(--pg-line)" }}
          >
            <div
              className="h-full rounded-full bg-purple-500 transition-[width] duration-150 ease-out"
              style={{ width: `${progressPct}%` }}
            />
            {started && !reduced ? (
              <div className="reward-tier-sweep pointer-events-none absolute inset-0 rounded-full" />
            ) : null}
          </div>
        </div>

        <div
          className="mt-3 flex items-stretch border-t pt-3 text-[9px] sm:text-[10px]"
          style={{ borderColor: "var(--pg-line)" }}
        >
          <div className="flex-1">
            <p className="font-medium" style={{ color: "var(--pg-fg)" }}>
              1 pt / $1
            </p>
            <p style={{ color: "var(--pg-muted)" }}>booth rent</p>
          </div>
          <div className="mx-2 w-px shrink-0" style={{ background: "var(--pg-line)" }} />
          <div className="flex-1 text-center">
            <p className="font-medium" style={{ color: "var(--pg-fg)" }}>
              2×
            </p>
            <p style={{ color: "var(--pg-muted)" }}>autopay</p>
          </div>
          <div className="mx-2 w-px shrink-0" style={{ background: "var(--pg-line)" }} />
          <div className="flex-1 text-right">
            <p className="font-semibold text-purple-400">{reachedPlatinum ? "1.5×" : "1×"}</p>
            <p style={{ color: "var(--pg-muted)" }}>{reachedPlatinum ? "Platinum" : "Bronze"} tier</p>
          </div>
        </div>
      </div>
    </div>
  )
}
